// src/admin/pages/ThemePage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Card,
  Input,
  message,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { themeService } from '../firebase/services';

const { Title, Text } = Typography;

const DEFAULTS = {
  '--black':  '#050505',
  '--white':  '#f0ece4',
  '--acid':   '#c8f04a',
  '--cyan':   '#00e5ff',
  '--muted':  '#2a2a2a',
  '--border': '#1e1e1e',
};

// ── Responsive hook ───────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── HexInput — free typing with validation ────────────────────────────────────
function HexInput({ value, onChange, style }) {
  const [draft, setDraft] = useState(value.toUpperCase());

  useEffect(() => {
    setDraft(value.toUpperCase());
  }, [value]);

  const handleChange = (e) => {
    let raw = e.target.value;
    if (!raw.startsWith('#')) raw = '#' + raw;
    raw = raw.toUpperCase();
    setDraft(raw);
    if (/^#[0-9A-F]{6}$/.test(raw)) onChange(raw);
  };

  const handleBlur = () => {
    if (!/^#[0-9A-F]{6}$/.test(draft)) setDraft(value.toUpperCase());
  };

  const isValid = /^#[0-9A-F]{6}$/.test(draft);

  return (
    <Input
      value={draft}
      maxLength={7}
      style={{ fontFamily: 'monospace', borderColor: isValid ? undefined : '#ff4d4f', ...style }}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}

// ── ColorPicker swatch (native input hidden under div) ────────────────────────
function ColorSwatch({ value, onChange, size = 36 }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{
        width: size, height: size,
        background: value,
        borderRadius: 6,
        border: '1px solid #d9d9d9',
        cursor: 'pointer',
      }} />
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
      />
    </div>
  );
}

// ── Mobile card for a single color entry ─────────────────────────────────────
function ColorCard({ varName, value, onColorChange, onResetOne, onDelete }) {
  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: '12px 14px' } }}>
      {/* Row 1: swatch + var name + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <ColorSwatch value={value} onChange={(hex) => onColorChange(varName, hex)} />

        <code style={{
          fontSize: 12,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {varName}
        </code>

        <Space size={4}>
          {DEFAULTS[varName] && value !== DEFAULTS[varName] && (
            <Tooltip title={`Reset to ${DEFAULTS[varName]}`}>
              <Button size="small" type="text" icon={<UndoOutlined />} onClick={() => onResetOne(varName)} />
            </Tooltip>
          )}
          <Popconfirm
            title="Delete this variable?"
            onConfirm={() => onDelete(varName)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      </div>

      {/* Row 2: hex input + preview bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <HexInput value={value} onChange={(hex) => onColorChange(varName, hex)} style={{ flex: 1 }} />
        <div style={{
          flex: '0 0 60px',
          height: 32,
          background: value,
          borderRadius: 4,
          border: '1px solid #d9d9d9',
        }} />
      </div>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ThemePage() {
  const [colors,   setColors]   = useState({ ...DEFAULTS });
  const [saved,    setSaved]    = useState({ ...DEFAULTS });
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [newName,  setNewName]  = useState('');
  const [newValue, setNewValue] = useState('#000000');

  const isMobile = useIsMobile();
  const isDirty  = JSON.stringify(colors) !== JSON.stringify(saved);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await themeService.get();
      const merged = data ? { ...DEFAULTS, ...data } : { ...DEFAULTS };
      setColors(merged);
      setSaved(merged);
    } catch {
      message.error('Failed to load theme');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await themeService.save(colors);
      setSaved({ ...colors });
      message.success('Theme saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleColorChange = (varName, hex) => setColors(prev => ({ ...prev, [varName]: hex }));

  const handleDelete = (varName) => setColors(prev => {
    const next = { ...prev };
    delete next[varName];
    return next;
  });

  const handleResetOne = (varName) => {
    if (DEFAULTS[varName]) setColors(prev => ({ ...prev, [varName]: DEFAULTS[varName] }));
  };

  const handleResetAll = () => {
    setColors({ ...DEFAULTS });
    message.info('Reset to defaults — save to apply');
  };

  const handleAdd = () => {
    const name = newName.trim().startsWith('--') ? newName.trim() : `--${newName.trim()}`;
    if (!name || name === '--') return message.warning('Enter a variable name');
    if (colors[name]) return message.warning('Variable already exists');
    setColors(prev => ({ ...prev, [name]: newValue }));
    setNewName('');
    setNewValue('#000000');
  };

  // ── Desktop table columns ─────────────────────────────────────────────────
  const columns = [
    {
      title: 'Variable',
      dataIndex: 'varName',
      key: 'varName',
      render: (name) => <code style={{ fontSize: 13 }}>{name}</code>,
    },
    {
      title: 'Color',
      dataIndex: 'value',
      key: 'color',
      width: 60,
      render: (value, { varName }) => (
        <ColorSwatch value={value} onChange={(hex) => handleColorChange(varName, hex)} />
      ),
    },
    {
      title: 'Hex',
      dataIndex: 'value',
      key: 'hex',
      width: 130,
      render: (value, { varName }) => (
        <HexInput value={value} onChange={(hex) => handleColorChange(varName, hex)} style={{ width: 110 }} />
      ),
    },
    {
      title: 'Preview',
      dataIndex: 'value',
      key: 'preview',
      width: 100,
      render: (value) => (
        <div style={{ width: 80, height: 28, background: value, borderRadius: 4, border: '1px solid #d9d9d9' }} />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, { varName, value }) => (
        <Space size={4}>
          {DEFAULTS[varName] && value !== DEFAULTS[varName] && (
            <Tooltip title={`Reset to ${DEFAULTS[varName]}`}>
              <Button size="small" type="text" icon={<UndoOutlined />} onClick={() => handleResetOne(varName)} />
            </Tooltip>
          )}
          <Popconfirm
            title="Delete this variable?"
            onConfirm={() => handleDelete(varName)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableData = Object.entries(colors).map(([varName, value]) => ({ key: varName, varName, value }));

  return (
    <div style={{ width: '100%', maxWidth: 700, boxSizing: 'border-box' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: 12,
        marginBottom: 24,
      }}>
        <Title level={4} style={{ margin: 0 }}>Color Theme</Title>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<UndoOutlined />} onClick={handleResetAll} style={{ flex: isMobile ? 1 : 'none' }}>
            Reset to Defaults
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty}
            style={{ flex: isMobile ? 1 : 'none' }}
          >
            Save
          </Button>
        </div>
      </div>

      {/* ── Color list ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : isMobile ? (
        <div style={{ marginBottom: 16 }}>
          {tableData.map(({ varName, value }) => (
            <ColorCard
              key={varName}
              varName={varName}
              value={value}
              onColorChange={handleColorChange}
              onResetOne={handleResetOne}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Add new color ── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 8,
        alignItems: isMobile ? 'stretch' : 'center',
      }}>
        <Input
          placeholder="--variable-name"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onPressEnter={handleAdd}
          style={{ fontFamily: 'monospace', flex: '1 1 180px' }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ColorSwatch value={newValue} onChange={setNewValue} size={32} />
          <Input
            value={newValue.toUpperCase()}
            maxLength={7}
            style={{ fontFamily: 'monospace', flex: 1, minWidth: 90 }}
            onChange={e => {
              let hex = e.target.value.trim();
              if (!hex.startsWith('#')) hex = '#' + hex;
              if (/^#[0-9a-fA-F]{6}$/.test(hex)) setNewValue(hex);
            }}
          />
          <Button icon={<PlusOutlined />} onClick={handleAdd} style={{ flexShrink: 0 }}>
            {isMobile ? null : 'Add'}
          </Button>
        </div>
      </div>

    </div>
  );
}